/**
 * Chart color palette — TIO brand colors with Peel-style depth.
 *
 * Each color has a main value plus gradient and glow variants for
 * canvas-based charts that use fills, shadows, and area gradients.
 */
/** The palette's danger red — over-limit tinting and the danger series must stay one color. */
export const CHART_DANGER_RED = '#dc2626';

export const DEFAULT_CHART_COLORS = [
  '#0e7490', // teal (ocean-600)
  '#6e44c1', // purple (violet-600)
  '#2dd4bf', // mint (brighter teal for contrast)
  '#998ce3', // lavender (violet-300)
  '#2563eb', // blue
  '#059669', // emerald
  '#f59e0b', // amber
  CHART_DANGER_RED,
  '#77bcc0', // light teal
  '#7c2d12', // brown
];

/**
 * Detect if dark mode is currently active via the document root element.
 */
export function isDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/**
 * Theme-aware color configuration for chart axes, grid lines, and text.
 * Dark mode uses the Peel-style muted tones; light mode stays crisp.
 */
export function getThemeColors() {
  const dark = isDarkMode();
  return {
    text: dark ? '#ececf0' : '#1f2937',
    textMuted: dark ? '#cbd5e1' : '#374151',
    textSubtle: dark ? '#94a3b8' : '#4b5563',
    grid: dark ? 'rgba(255,255,255,0.14)' : '#d1d5db',
    axis: dark ? '#94a3b8' : '#6b7280',
    background: 'transparent',
    tooltipBg: dark ? 'rgba(15,16,18,0.92)' : '#ffffff',
    tooltipText: dark ? '#ececf0' : '#1f2937',
    tooltipBorder: dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    // Peel-style glow accents
    glowTeal: dark ? 'rgba(45,212,191,0.15)' : 'rgba(14,116,144,0.08)',
    glowViolet: dark ? 'rgba(124,127,255,0.15)' : 'rgba(110,68,193,0.08)',
  };
}

/**
 * Convert hex color to rgba string.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
